import React, { useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { CategoryEntityType } from "@/types/category";
import {
  useCategories,
  useReorderCategories,
} from "@/hooks/useCategories.hooks";
import { ReorderEntityType } from "@/services/interfaces/ICategoryService";
import { useHotel } from "@/hooks/useHotel";
import { useDebounce } from "@/hooks/useDebounce";
import { Input } from "@/components/ui/input";
import { CategoryItem } from "./CategoryItem";
import { CreateCategoryButton } from "./CreateCategoryButton";
import { cn } from "@/lib/utils";

interface CategorySectionProps {
  entityType: CategoryEntityType;
  title: string;
  className?: string;
  selectedCategoryId?: string;
  onCategorySelect?: (id: string) => void;
}

export const CategorySection: React.FC<CategorySectionProps> = ({
  entityType,
  title,
  className,
  selectedCategoryId,
  onCategorySelect,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [page, setPage] = useState(1);

  const showPriority = entityType === CategoryEntityType.FAQ;
  const limit = 6;

  const { data, isLoading } = useCategories({
    hotelId: hotelId || "",
    entityType,
    search: debouncedSearch,
    page,
    limit,
  });

  const reorderCategories = useReorderCategories();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && data?.items && hotelId) {
      const oldIndex = data.items.findIndex((item) => item.id === active.id);
      const newIndex = data.items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(data.items, oldIndex, newIndex);

      reorderCategories.mutate({
        ids: newItems.map((item) => item.id),
        hotelId,
        type: ReorderEntityType.CATEGORY,
        startIndex: (page - 1) * limit,
      });
    }
  };

  return (
    <div
      className={cn(
        "border-base-border relative flex h-full flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm",
        className,
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-black/96">{title}</h2>
          <CreateCategoryButton entityType={entityType} />
        </div>
        <div className="relative">
          <Input
            type="search"
            placeholder={t("categories:searchCategories")}
            className="pl-9"
            icon={<Search size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            {t("categories:loading")}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={data?.items.map((i) => i.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-2">
                {data?.items.map((category) => (
                  <div
                    key={category.id}
                    onClick={() => onCategorySelect?.(category.id)}
                    className="cursor-pointer"
                  >
                    <CategoryItem
                      category={category}
                      showPriority={showPriority}
                      isActive={selectedCategoryId === category.id}
                    />
                  </div>
                ))}
                {!isLoading && data?.items.length === 0 && (
                  <div className="text-muted-foreground py-8 text-center">
                    {t("categories:noCategoriesFound")}
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <Pagination className="border-base-border mt-auto border-t pt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              className={cn(
                "cursor-pointer",
                page === 1 && "pointer-events-none opacity-50",
              )}
              onClick={() => setPage(Math.max(1, page - 1))}
            />
          </PaginationItem>
          {data &&
            Array.from({ length: data.totalPages }, (_, i) => i + 1).map(
              (p) => (
                <PaginationItem key={p}>
                  <PaginationLink
                    isActive={page === p}
                    onClick={() => setPage(p)}
                    className="cursor-pointer"
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
          <PaginationItem>
            <PaginationNext
              className={cn(
                "cursor-pointer",
                (!data || page === data.totalPages) &&
                  "pointer-events-none opacity-50",
              )}
              onClick={() => setPage(page + 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
